import React, { FC, useEffect, useMemo, useState } from 'react'
import type { AllotmentAdvanceRecord } from './ViewAllotmentAdvanceModal'
import { useAuth } from '../../auth'
import VesselLandingLink from '../../../shared/components/VesselLandingLink'

export type AllotmentAdvanceWorkflowStatus = 'DRAFT' | 'PENDING_MASTER_REVIEW' | 'MASTER_REVIEWED' | 'REVIEWED' | 'SUBMITTED'

type Props = {
  visible: boolean
  record: AllotmentAdvanceRecord | null
  status: AllotmentAdvanceWorkflowStatus
  initialRemarks?: string
  onClose: () => void
  onSave: (officeRemarks: string) => void
  onCloseSatisfactory: (officeRemarks: string) => void
  isLoading?: boolean
}

const WRAP_STYLE: React.CSSProperties = {
  background: 'rgba(0,0,0,0.5)',
  position: 'fixed',
  inset: 0,
  zIndex: 1050,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

const LABEL = 'form-label fw-semibold fs-6 mb-2 text-dark'

const getStatusDisplay = (status: AllotmentAdvanceWorkflowStatus) => {
  if (status === 'PENDING_MASTER_REVIEW') return { label: 'Pending Master Review', badgeClass: 'badge-light-info' }
  if (status === 'MASTER_REVIEWED') return { label: 'Ready for Office Review', badgeClass: 'badge-light-warning' }
  if (status === 'REVIEWED') return { label: 'Reviewed', badgeClass: 'badge-light-success' }
  return { label: 'Draft', badgeClass: 'badge-light-secondary' }
}

const AllotmentAdvanceReviewModal: FC<Props> = ({
  visible,
  record,
  status,
  initialRemarks = '',
  onClose,
  onSave,
  onCloseSatisfactory,
  isLoading = false,
}) => {
  const { hasPermission } = useAuth()
  const canSaveReviewOnly = hasPermission('UPDATE_CREW_ALLOTMENT')
  const canCloseSatisfactory = hasPermission('APPROVE_CREW_ALLOTMENT')

  const [officeRemarks, setOfficeRemarks] = useState('')
  const [remarksError, setRemarksError] = useState('')

  useEffect(() => {
    if (!visible || !record) return
    setOfficeRemarks(initialRemarks || '')
    setRemarksError('')
  }, [visible, record, initialRemarks])

  const statusMeta = useMemo(() => getStatusDisplay(status), [status])

  const validateRemarks = () => {
    if (!officeRemarks || !officeRemarks.trim()) {
      setRemarksError('Office remarks are required.')
      return false
    }
    setRemarksError('')
    return true
  }

  if (!visible || !record) return null

  return (
    <div style={WRAP_STYLE} tabIndex={-1}>
      <div
        className='bg-white text-dark rounded shadow-lg d-flex flex-column'
        style={{
          width: '70vw',
          maxWidth: '900px',
          maxHeight: '80vh',
        }}
      >
        <div className='d-flex align-items-center justify-content-between border-bottom px-5 py-4 flex-shrink-0'>
          <div>
            <h5 className='modal-title text-dark m-0'>Office Review - Allotment and Advance</h5>
            <div className='text-muted small mt-1'>
              {record.crewName || '-'} | {record.rankName || '-'} |{' '}
              <VesselLandingLink
                vesselId={record.vesselId ?? null}
                vesselName={record.vesselName || '-'}
                className='fw-semibold text-dark text-hover-primary'
              />
            </div>
          </div>
          <div className='d-flex align-items-center gap-3'>
            <span className={`badge ${statusMeta.badgeClass}`}>{statusMeta.label}</span>
            <button type='button' className='btn-close m-0' onClick={onClose} disabled={isLoading} />
          </div>
        </div>

        <div className='flex-grow-1 overflow-auto px-5 py-4'>
          <div className='mb-4'>
            <label className={LABEL}>
              Office Remarks <span style={{ color: '#dc3545' }}>*</span>
            </label>
            <textarea
              className='form-control'
              rows={4}
              value={officeRemarks}
              onChange={(event) => {
                setOfficeRemarks(event.target.value)
                if (remarksError) setRemarksError('')
              }}
              placeholder='Add office review remarks'
              disabled={isLoading}
            />
            {remarksError && <div className='text-danger small mt-2'>{remarksError}</div>}
          </div>
        </div>

        <div className='border-top d-flex justify-content-end gap-2 px-5 py-4 flex-shrink-0'>
          <button type='button' className='btn btn-light btn-sm' onClick={onClose} disabled={isLoading}>
            Cancel
          </button>
          <button
            type='button'
            className='btn btn-light-primary btn-sm'
            disabled={isLoading || !canSaveReviewOnly}
            onClick={() => {
              if (!canSaveReviewOnly || !validateRemarks()) return
              onSave(officeRemarks)
            }}
          >
            Save Review Only
          </button>
          <button
            type='button'
            className='btn btn-success btn-sm'
            disabled={isLoading || !canCloseSatisfactory}
            onClick={() => {
              if (!canCloseSatisfactory || !validateRemarks()) return
              onCloseSatisfactory(officeRemarks)
            }}
          >
            Closed Satisfactory
          </button>
        </div>
      </div>
    </div>
  )
}

export default AllotmentAdvanceReviewModal
